/**
 * Social Media Agent Service
 * Rules the moderator applies to Facebook and Instagram comments.
 */

import { withBuScope } from '@/lib/business-unit-scope';
import { api } from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type {
	ReplyDelay,
	SocialActionLogPage,
	SocialAgentSettings,
	SocialRule,
	SocialRuleStatus
} from '@/modules/social-agent/types';
import {
	toActionLogQueryParams,
	type ActionLogQueryInput
} from '@/modules/social-agent/action-log-query';

/** Every list/read endpoint returns the standard `{ statusCode, data, message, success }` envelope. */
interface ApiEnvelope<T> {
	data: T;
}

export interface CreateSocialRulePayload {
	name: string;
	description?: string;
	condition: string;
	actionType: SocialRule['actionType'];
	actionOptions?: SocialRule['actionOptions'];
	priority?: number;
	channelScope?: SocialRule['channelScope'];
}

export type UpdateSocialRulePayload = Partial<CreateSocialRulePayload>;

/** How many rows one page of the action log asks for. The server's own default; its ceiling is 200. */
export const ACTION_LOG_PAGE_SIZE = 50;

/**
 * An export that has been ACCEPTED, not delivered.
 *
 * The server builds the file in a worker and emails it, so there is nothing to hand back but the
 * address it is going to. Truncation is no longer knowable here — the row count is only settled
 * once the job runs — so the file and the email body carry that warning instead.
 */
export interface ActionLogExportRequest {
	email: string;
}

export const socialAgentService = {
	async listRules(scope?: { bu_id?: string }): Promise<SocialRule[]> {
		const { data } = await api.get<ApiEnvelope<SocialRule[]>>(
			withBuScope(API_ENDPOINTS.SOCIAL_AGENT.RULES, scope ?? {})
		);
		return data.data ?? [];
	},

	async getRule(ruleId: string): Promise<SocialRule> {
		const { data } = await api.get<ApiEnvelope<SocialRule>>(API_ENDPOINTS.SOCIAL_AGENT.RULE(ruleId));
		return data.data;
	},

	/**
	 * A rule created while a brand is selected belongs to THAT brand. Created with no brand it is
	 * shared, which is what every rule was before brands existed.
	 */
	async createRule(payload: CreateSocialRulePayload, scope?: { bu_id?: string }): Promise<SocialRule> {
		const { data } = await api.post<ApiEnvelope<SocialRule>>(
			withBuScope(API_ENDPOINTS.SOCIAL_AGENT.RULES, scope ?? {}),
			payload
		);
		return data.data;
	},

	async updateRule(ruleId: string, payload: UpdateSocialRulePayload): Promise<SocialRule> {
		const { data } = await api.patch<ApiEnvelope<SocialRule>>(API_ENDPOINTS.SOCIAL_AGENT.RULE(ruleId), payload);
		return data.data;
	},

	/**
	 * Enable/disable. Its own endpoint because it is the one write a Kim-authored rule accepts
	 * besides channel scope — routing it through the general update would hit the immutability 403.
	 */
	async setRuleStatus(ruleId: string, status: SocialRuleStatus): Promise<SocialRule> {
		const { data } = await api.patch<ApiEnvelope<SocialRule>>(API_ENDPOINTS.SOCIAL_AGENT.RULE_STATUS(ruleId), {
			status
		});
		return data.data;
	},

	async deleteRule(ruleId: string): Promise<void> {
		await api.delete(API_ENDPOINTS.SOCIAL_AGENT.RULE(ruleId));
	},

	async getSettings(scope?: { bu_id?: string }): Promise<SocialAgentSettings> {
		const { data } = await api.get<ApiEnvelope<SocialAgentSettings>>(
			withBuScope(API_ENDPOINTS.SOCIAL_AGENT.SETTINGS, scope ?? {})
		);
		return data.data ?? { replyDelay: null };
	},

	/** `null` clears the delay. The field is always sent, so an empty selection is explicit. */
	async setReplyDelay(replyDelay: ReplyDelay | null, scope?: { bu_id?: string }): Promise<SocialAgentSettings> {
		const { data } = await api.patch<ApiEnvelope<SocialAgentSettings>>(
			withBuScope(API_ENDPOINTS.SOCIAL_AGENT.SETTINGS, scope ?? {}),
			{ replyDelay }
		);
		return data.data;
	},

	/**
	 * One page of the action log, newest first.
	 *
	 * Keyset paginated: `pagination.nextCursor` is the only way to the next page. There is no page
	 * number and no total, and none should be synthesised here — the server declines to count an
	 * append-only table on every request, and a number invented client-side would be wrong the
	 * moment a comment arrives.
	 */
	async listActionLogs(input: ActionLogQueryInput, scope?: { bu_id?: string }): Promise<SocialActionLogPage> {
		// Merged into the same params object as the filters: the endpoint rejects unknown query
		// parameters outright, so the brand has to be a parameter it knows rather than an extra.
		const params = { ...toActionLogQueryParams(input), ...(scope?.bu_id ? { bu_id: scope.bu_id } : {}) };
		console.info('[socialAgentService.listActionLogs] Requesting action logs', params);

		const { data } = await api.get<ApiEnvelope<SocialActionLogPage>>(API_ENDPOINTS.SOCIAL_AGENT.LOGS, { params });
		const page = data.data;

		console.info('[socialAgentService.listActionLogs] Received action logs', {
			returned: page?.logs?.length ?? 0,
			hasMore: page?.pagination?.hasMore ?? false
		});

		return page;
	},

	/**
	 * Ask the server to email the export.
	 *
	 * `limit` and `cursor` are deliberately dropped: the export is the whole filtered set up to the
	 * server's cap, not the rows that happen to be on screen. Sending the page's cursor would export
	 * everything EXCEPT what the person is looking at.
	 *
	 * POST rather than GET, and the filters go in the query string as they do for the list, so the
	 * two endpoints keep one filter contract. The recipient goes in the body; the server falls back
	 * to the signed-in user's address when it is omitted.
	 */
	async requestActionLogExport(
		input: Omit<ActionLogQueryInput, 'limit' | 'cursor'>,
		email: string,
		scope?: { bu_id?: string }
	): Promise<ActionLogExportRequest> {
		// The CSV has to hold the rows the page was showing. An account-wide export mailed out from
		// a brand-scoped view puts other brands' customers and their public comments in an inbox.
		const params = { ...toActionLogQueryParams(input), ...(scope?.bu_id ? { bu_id: scope.bu_id } : {}) };
		console.info('[socialAgentService.requestActionLogExport] Requesting export', { ...params, email });

		// `{}` rather than null when there is no address: axios serializes null as a bare `null`
		// body, which the JSON parser rejects outright as malformed.
		const { data } = await api.post<ApiEnvelope<ActionLogExportRequest>>(
			API_ENDPOINTS.SOCIAL_AGENT.LOGS_EXPORT,
			email ? { email } : {},
			{ params }
		);

		console.info('[socialAgentService.requestActionLogExport] Export queued', { email: data.data?.email });
		return data.data;
	}
};
